import { useState } from 'react';
import type { FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useBasketAvailability } from '../../../../bff-api/baskets/useBasketAvailability';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { GetIcon } from '../../../../components/icons';
import { ResponsiveImage } from '../../../../components/responsive-image';
import { Button, Text } from '../../../../ui-components';
import { useCustomPreferenceOrDefault } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { useCartState } from '../../contexts/useCartState';
import { useCulinaryDrawer } from '../../contexts/useCulinaryDrawer';
import { isExpiredCulinaryClass } from '../../utils/cart-utils';
import { CartItemPrice } from '../cart-item-price/CartItemPrice';
import { DeleteProductItemButton } from '../cart-item/DeleteProductItemButton';
import { ProductLink } from '../cart-item/ProductLink';
import { PromotionBadges } from '../cart-item/PromotionBadges';
import { Attendee } from './Attendee';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type {
    CartCulinaryClass,
    CartCulinaryProductItem,
} from '../../models/CartCulinaryProductItem';

export interface AggregatedCulinaryItem
    extends Omit<ProductItemEditor, 'c_culinaryClass' | 'c_productType'>,
        CartCulinaryProductItem {}

export interface CulinaryItemProps {
    productItem: AggregatedCulinaryItem;
    isSoftLink?: boolean;
}

export const CulinaryItem: FC<CulinaryItemProps> = ({ productItem, isSoftLink }) => {
    const { onDeleteAttendee, onOpenEditCulinaryDrawer } = useCulinaryDrawer();
    const {
        isLoading,
        data: basketAvailability,
        isError: isBasketAvailabilityError,
        error: basketAvailabilityError,
    } = useBasketAvailability();
    const { refManager } = useCartState();

    if (isBasketAvailabilityError) {
        throw basketAvailabilityError;
    }

    const [canShowNoMoreSeatsMessage, setCanShowNoMoreSeatsMessage] = useState(false);

    const storeQuantity = basketAvailability?.itemsAvailableToSell[productItem.productId];
    const outOfAvailability =
        typeof productItem?.quantity === 'number' &&
        productItem.quantity > (storeQuantity?.availableToSell ?? 0);
    const noMoreSeatsAvailable =
        productItem.quantity === storeQuantity?.availableToSell || outOfAvailability;

    const findAttendeeForEdit = (attendeeId: string, index: number): void => {
        const interactedAttendee = productItem.c_culinaryClass?.attendees.find(
            (attendee) => attendee.id === attendeeId,
        );

        if (productItem && productItem.itemId) {
            onOpenEditCulinaryDrawer('edit', productItem.itemId, interactedAttendee, index);
        }
    };

    const deleteAttendee = (attendeeId: string): void => {
        onDeleteAttendee(productItem, attendeeId);
    };

    const onAddAttendee = (): void => {
        setCanShowNoMoreSeatsMessage(true);

        if (!noMoreSeatsAvailable && productItem && productItem.itemId) {
            onOpenEditCulinaryDrawer('add', productItem.itemId);
        }
    };

    const classBlockedThreshold = useCustomPreferenceOrDefault(
        CustomPreference.CULINARY_CLASS_BLOCKED_THRESHOLD,
        0,
    );

    if (isLoading) {
        return null;
    }

    const isExpiredClass = isExpiredCulinaryClass(productItem, classBlockedThreshold);
    const isAddAttendeeDisabled = canShowNoMoreSeatsMessage && noMoreSeatsAvailable;
    const isOutOfStock = !(storeQuantity?.availableToSell ?? 0);

    return (
        <Flex
            wrap="wrap"
            gap={{ base: '1.5rem 1rem', 'lg-sm': '1.5rem 2rem' }}
            alignItems="flex-start"
            padding={{ base: '2rem 0' }}
            borderBottom="1px solid #C6C6C6"
            _first={{ paddingTop: 0 }}
            _last={{ paddingBottom: 0, borderBottom: 'none' }}
            data-testid={`cart-item-culinary-class-${productItem.productId}`}
            ref={refManager(productItem.productId)}
        >
            <Flex maxW="120px">
                <Box width="120px" height="120px">
                    <ProductLink c_pwaUrl={productItem.c_pwaUrl} isSoftLink={isSoftLink}>
                        <ResponsiveImage
                            id={`cart-item-${productItem.productId}-image`}
                            src={productItem.c_shoppingBagImageUrl ?? ''}
                            alt={productItem.productName}
                            width="120px"
                            height="120px"
                        />
                    </ProductLink>
                </Box>
            </Flex>
            <Flex
                shrink={0}
                grow={1}
                basis={0}
                wrap="wrap"
                gap={4}
                direction={{ base: 'column', md: 'row' }}
                justifyContent="space-between"
            >
                <Flex gap={4} wrap="wrap" direction="column">
                    <Flex wrap="wrap" direction="column" gap={1}>
                        <Text style="bold">
                            <ProductLink c_pwaUrl={productItem.c_pwaUrl} isSoftLink={isSoftLink}>
                                {productItem.productName}
                            </ProductLink>
                        </Text>
                        <Text>
                            <FormattedMessage id="cart.culinary-item.date" defaultMessage="Date" />:{' '}
                            {productItem?.c_culinaryClass?.date || ''}
                        </Text>
                        <Text>
                            <FormattedMessage id="cart.culinary-item.chef" defaultMessage="Chef" />:{' '}
                            {productItem?.c_culinaryClass?.instructor || ''}
                        </Text>
                    </Flex>
                    <Flex gap={1}>
                        <GetIcon name="union" size="sm" />
                        <Text id="class-location">
                            {getClassLocation(productItem?.c_culinaryClass)}
                        </Text>
                    </Flex>
                </Flex>
                <Flex gap={4} direction="column">
                    <Flex width="100%" justifyContent={{ base: 'flex-start', 'lg-sm': 'end' }}>
                        <CartItemPrice productItem={productItem} />
                    </Flex>
                </Flex>
            </Flex>
            <Flex width="100%" direction="column" gap={4}>
                {!isOutOfStock &&
                    productItem.c_culinaryClass?.attendees.map((attendee, index) => (
                        <Attendee
                            numberOfAttendees={productItem.c_culinaryClass?.attendees.length ?? 0}
                            key={attendee.id}
                            attendee={attendee}
                            index={index}
                            onEdit={findAttendeeForEdit}
                            onDelete={deleteAttendee}
                            showButtons={!isExpiredClass}
                        />
                    ))}

                <Flex align="center" gap={4} direction="column" alignItems="flex-start">
                    {!isOutOfStock && !outOfAvailability && !isExpiredClass && (
                        <Button
                            id="add-attendee"
                            variant="tertiary"
                            padding="0"
                            isDisabled={isAddAttendeeDisabled}
                            rightIcon={<GetIcon name="plus" size="md" />}
                            onClick={onAddAttendee}
                        >
                            <FormattedMessage
                                id="cart.culinary-item.add-attendee"
                                defaultMessage="Add Attendee"
                            />
                        </Button>
                    )}
                    {(isAddAttendeeDisabled || isExpiredClass) && (
                        <Text id="class-no-more-seats-error-message" color="slt-red-error">
                            <FormattedMessage
                                id="cart.culinary-item.not-available"
                                defaultMessage="There are no more seats available for this class"
                            />
                        </Text>
                    )}
                    {isOutOfStock && !isExpiredClass && (
                        <Text id="class-sold-out-error-message" color="slt-red-error">
                            <FormattedMessage
                                id="cart.culinary-item.sold.out"
                                defaultMessage="This class is sold out"
                            />
                        </Text>
                    )}
                    {outOfAvailability && !isOutOfStock && !isExpiredClass && (
                        <Text id="class-out-of-availability-error-message" color="slt-red-error">
                            <FormattedMessage
                                id="cart.culinary-item.outOfAvailability"
                                defaultMessage="Only {seats} {label} left. Please adjust the number of attendees."
                                values={{
                                    seats: Math.floor(storeQuantity?.availableToSell ?? 0),
                                    label: storeQuantity?.availableToSell === 1 ? 'seat' : 'seats',
                                }}
                            />
                        </Text>
                    )}
                    <PromotionBadges {...productItem} />
                    <ConditionalRender visibility={!!productItem.c_isExcludedFromPromotion}>
                        <Text
                            data-testid="cart-item-promotion-badge-excluded-from-promo"
                            color="slt-red-error"
                        >
                            <FormattedMessage
                                id="cart-item.badge.excluded-from-promo"
                                defaultMessage="Excluded From Promo"
                            />
                        </Text>
                    </ConditionalRender>
                </Flex>
            </Flex>

            <Flex width="100%" gap={4} marginTop={{ base: 2, sm: -2 }}>
                <DeleteProductItemButton productItem={productItem} />
            </Flex>
        </Flex>
    );
};

CulinaryItem.defaultProps = {
    isSoftLink: false,
};

function getClassLocation(culinaryClass: CartCulinaryClass): string {
    return !culinaryClass.isOnline
        ? `${culinaryClass.location.address1}, ${culinaryClass.location.city}, ${culinaryClass.location.stateCode} ${culinaryClass.location.postalCode}`
        : culinaryClass?.location?.name;
}
